/**
 * The partner portal's own frame: a small sidebar and top bar, driven by the
 * partner's portal role (individual / company admin / team member), not by
 * iam screen grants. Looks like the CRM shell (same stylesheet) but offers only
 * what a partner may use. Internal CRM screens are not reachable from here.
 */
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth.js';
import { toggleSidebar, setSidebarOpen, dismissToast } from '../ui/uiSlice.js';
import ToastStack from '../../components/layout/ToastStack.jsx';
import { Dropdown } from '../../components/layout/AppShell.jsx';
import { Avatar, TopProgressBar, PageLoader, ErrorState } from '../../components/common/index.jsx';
import * as api from '../../api/partnerPortal.api.js';
import { usePartnerMe, HOME, ROLE_NAME } from './partnerShared.jsx';
import { relativeTime } from '../../utils/formatters.js';

const NAV = (role) => [
  { to: HOME[role], icon: 'bi-grid-1x2-fill', label: role === 'admin' ? 'Company Dashboard' : 'Dashboard' },
  ...(role === 'admin' ? [{ to: '/partner/team-dashboard', icon: 'bi-people-fill', label: 'Team Dashboard' }] : []),
  { to: '/partner/leads', icon: 'bi-magnet-fill', label: 'Deals / Leads' },
  { to: '/partner/projects', icon: 'bi-kanban-fill', label: 'Projects' },
  ...(role === 'admin' ? [{ to: '/partner/team', icon: 'bi-person-gear', label: 'Team Users' }] : []),
  { to: '/partner/notifications', icon: 'bi-bell-fill', label: 'Notifications' },
  { to: '/partner/profile', icon: 'bi-person-circle', label: 'Profile' },
];

export default function PartnerShell() {
  const { user, signOut } = useAuth();
  const { sidebarCollapsed, sidebarOpen, toasts } = useSelector((s) => s.ui);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const meQ = usePartnerMe();
  const dismiss = useCallback((id) => dispatch(dismissToast(id)), [dispatch]);

  if (meQ.isLoading) return <PageLoader label="Opening your partner portal" />;
  if (meQ.isError) return <div className="p-4"><ErrorState error={meQ.error} onRetry={meQ.refetch} /></div>;
  const me = meQ.data;
  if (location.pathname === '/partner' || location.pathname === '/partner/') return <Navigate to={me.dashboard.replace('/partner', '/partner')} replace />;

  const items = NAV(me.role);
  const out = async () => { await signOut(); navigate('/login', { replace: true }); };
  const toggle = () => (window.matchMedia('(min-width: 992px)').matches ? dispatch(toggleSidebar()) : dispatch(setSidebarOpen(!sidebarOpen)));

  return (
    <div className={`app ${sidebarCollapsed ? 'nav-collapsed' : ''}`}>
      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <div className="brand-mark"><img src="/logo-icon.png" alt="EnGage" /></div>
          <div className="brand-text"><div className="brand-name"><img src="/logo-full.png" alt="EnGage" className="brand-logo-hero-sidebar" /></div></div>
        </div>
        <nav className="sidebar-scroll" aria-label="Partner navigation">
          <div className="nav-group"><div className="nav-group-label"><span>{me.partner.name}</span></div>
            <div className="nav-group-items"><div>
              {items.map((i) => (
                <NavLink key={i.to} to={i.to} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={() => dispatch(setSidebarOpen(false))}
                  title={sidebarCollapsed ? i.label : undefined} data-rail-label={i.label}>
                  <i className={`bi ${i.icon}`} aria-hidden="true" /><span className="nav-text">{i.label}</span>
                </NavLink>
              ))}
            </div></div>
          </div>
        </nav>
        <div className="sidebar-foot">
          <button type="button" className="rail-toggle" onClick={toggle} aria-label="Collapse navigation"><i className="bi bi-chevron-double-left" aria-hidden="true" /><span className="rail-text">Collapse menu</span></button>
          <div className="sidebar-user">
            <Avatar name={user?.full_name ?? '?'} size={27} />
            <div className="flex-grow-1 min-w-0 user-text"><div className="name text-truncate">{user?.full_name}</div><div className="role text-truncate">{ROLE_NAME[me.role]}</div></div>
          </div>
        </div>
      </aside>
      <div className={`sidebar-backdrop ${sidebarOpen ? 'show' : ''}`} onClick={() => dispatch(setSidebarOpen(false))} aria-hidden="true" />

      <div className="main">
        <header className="topbar">
          <button type="button" className="icon-btn" onClick={toggle} aria-label="Toggle navigation"><i className="bi bi-list" aria-hidden="true" /></button>
          <div className="t-sm fw-650 ms-2 d-none d-md-block">{me.partner.name} <span className="text-muted fw-400">· {me.partner.code}</span></div>
          <div className="ms-auto d-flex align-items-center gap-1">
            <NavLink to="/partner/leads?new=1" className="btn btn-add btn-sm-x d-flex align-items-center gap-1"><i className="bi bi-plus-lg" aria-hidden="true" /><span className="d-none d-md-inline">Add lead</span></NavLink>
            <BellMenu />
            <Dropdown className="dropdown" menuClassName="dropdown-menu-end"
              renderToggle={(p) => <button type="button" className="d-block ms-1 border-0 bg-transparent p-0" aria-label="Account" {...p}><Avatar name={user?.full_name ?? '?'} size={27} className="av-ring" /></button>}>
              <li className="px-2 py-2"><div className="t-sm fw-650">{user?.full_name}</div><div className="t-2xs ink-3">{user?.email}</div></li>
              <li><hr className="dropdown-divider" /></li>
              <li><NavLink className="dropdown-item" to="/partner/profile"><i className="bi bi-person me-2" />Profile</NavLink></li>
              <li><NavLink className="dropdown-item" to="/profile/password"><i className="bi bi-key me-2" />Change password</NavLink></li>
              <li><hr className="dropdown-divider" /></li>
              <li><button type="button" className="dropdown-item danger w-100 text-start border-0 bg-transparent" onClick={out}><i className="bi bi-box-arrow-right me-2" />Sign out</button></li>
            </Dropdown>
          </div>
        </header>
        <main className="page"><Outlet context={me} /></main>
      </div>
      <TopProgressBar />
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}

function BellMenu() {
  const q = useQuery({ queryKey: ['partner', 'notifications', 'bell'], queryFn: () => api.notifications({ limit: 6 }), refetchInterval: 60_000 });
  const unread = q.data?.unread ?? 0;
  return (
    <Dropdown className="dropdown" menuClassName="dropdown-menu-end p-0"
      renderToggle={(p) => (
        <button type="button" className="icon-btn position-relative" aria-label="Notifications" {...p}>
          <i className="bi bi-bell" aria-hidden="true" />
          {unread > 0 && <span className="position-absolute badge rounded-pill bg-danger" style={{ top: 2, right: 0, fontSize: 9 }}>{unread > 9 ? '9+' : unread}</span>}
        </button>
      )}>
      <li><div className="d-flex align-items-center justify-content-between px-3 pt-3 pb-2" style={{ width: 340 }}><div className="fw-650">Notifications</div><NavLink to="/partner/notifications" className="t-xs">View all</NavLink></div></li>
      {(q.data?.data ?? []).length === 0 && <li><div className="px-3 pb-3 t-sm text-muted">Nothing yet.</div></li>}
      {(q.data?.data ?? []).map((n) => (
        <li key={n.id}><NavLink className="dropdown-item py-2" style={{ whiteSpace: 'normal' }} to={n.partner_lead_id ? `/partner/leads/${n.partner_lead_id}` : '/partner/notifications'}>
          <div className={`t-sm ${n.is_read ? '' : 'fw-650'}`}>{n.title}</div><div className="t-2xs ink-3">{relativeTime(n.created_at)}</div></NavLink></li>
      ))}
    </Dropdown>
  );
}
